/**
 * One predicate for "is this image source worth rendering?".
 *
 * Three separate things all read as a usable image URL but render as the
 * browser's broken-image icon:
 *  - `""`/undefined passed through {@link assetUrl}, which returns `"/"`
 *    for an empty input — truthy, so every `src ? <img/> : null` guard
 *    passes and the browser fetches the SPA's HTML as an image;
 *  - the create form's `"/img/image_placeholder.jpg"` default, which was
 *    persisted as a REAL logoUrl for orgs created without a logo (the org
 *    detail page filtered it out, the index card did not — which is why
 *    the same org showed a grey squiggle in one place and nothing in the
 *    other);
 *  - blank/whitespace strings from legacy payloads.
 *
 * The org detail page grew this filter inline; it now lives here so index
 * cards, banners, featured slots and the create form all agree.
 */
export function isRenderableImageSrc(src?: string | null): boolean {
    if (!src) return false;
    const trimmed = String(src).trim();
    if (!trimmed || trimmed === "/") return false;
    if (/image_placeholder/i.test(trimmed)) return false;
    return true;
}

/** The renderable src, or `null` — handy for `const s = imageSrcOrNull(x)`. */
export function imageSrcOrNull(src?: string | null): string | null {
    return isRenderableImageSrc(src) ? String(src).trim() : null;
}

/**
 * Normalize a value on its way INTO storage: placeholder/empty inputs
 * become `""` so nothing downstream has to special-case them again.
 */
export function normalizeStoredImageSrc(src?: string | null): string {
    return isRenderableImageSrc(src) ? String(src).trim() : "";
}
